import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Smartphone, Shield, Zap, Users, Download, Eye } from 'lucide-react';

const DemoSection = () => {
  const features = [
    {
      icon: Smartphone,
      title: "Upload from phone",
      description: "Snap a video after class, upload instantly"
    },
    {
      icon: Users,
      title: "Family access control",
      description: "Parents see only their children's content"
    },
    {
      icon: Shield,
      title: "No ads, ever",
      description: "Private, secure platform built for families"
    }
  ];

  const screenshots = [
    {
      title: "Admin Upload",
      description: "Simple upload from any device",
      image: "/api/placeholder/300/600"
    },
    {
      title: "Family Selection",
      description: "Choose which families see the video",
      image: "/api/placeholder/300/600"
    },
    {
      title: "Parent View",
      description: "Clean, ad-free viewing experience",
      image: "/api/placeholder/300/600"
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <Badge variant="secondary" className="bg-blue-100 text-blue-700 mb-4">
            See it in action
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Watch HiveShare Work Its Magic
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From upload to parent notification in under 30 seconds. 
            See how easy video sharing can be.
          </p>
        </motion.div>

        {/* Demo Video Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-2xl overflow-hidden">
            {/* Video Placeholder */}
            <div className="aspect-video bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center relative">
              <div className="text-center text-white">
                <div className="bg-white bg-opacity-20 rounded-full p-8 mb-6 inline-block backdrop-blur-sm">
                  <Play className="h-16 w-16 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-2">2-Minute Demo</h3>
                <p className="text-blue-100 text-lg">See the complete workflow</p>
              </div>
              
              {/* Play Button Overlay */}
              <Button
                size="lg"
                className="absolute inset-0 w-full h-full bg-transparent hover:bg-white hover:bg-opacity-10 border-0 text-white"
              >
                <Play className="h-20 w-20" />
              </Button>
            </div>

            {/* Video Controls */}
            <div className="bg-gray-900 p-4 flex items-center justify-between">
              <div className="flex items-center space-x-4 text-white">
                <div className="flex items-center space-x-2">
                  <Eye className="h-4 w-4" />
                  <span className="text-sm">1,247 views</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Download className="h-4 w-4" />
                  <span className="text-sm">Watch offline</span>
                </div>
              </div>
              <div className="text-sm text-gray-400">
                2:15 duration
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Wins */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="max-w-5xl mx-auto mb-16"
        >
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="text-center border-2 border-blue-100 hover:border-blue-200 transition-all duration-300 hover:shadow-lg">
                  <CardContent className="p-8">
                    <div className="bg-blue-100 text-blue-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                      <feature.icon className="h-8 w-8" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Screenshot Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="max-w-6xl mx-auto"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-12">
            Simple Workflow, Powerful Results
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {screenshots.map((screenshot, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="text-center"
              >
                <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-8 mb-6 h-80 flex items-center justify-center">
                  <div className="text-center">
                    <div className="bg-blue-600 rounded-lg p-4 mb-4 inline-block">
                      <Smartphone className="h-8 w-8 text-white" />
                    </div>
                    <p className="text-gray-600">Screenshot placeholder</p>
                    <p className="text-sm text-gray-500">{screenshot.title}</p>
                  </div>
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  {screenshot.title}
                </h4>
                <p className="text-gray-600">
                  {screenshot.description}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center mt-16"
        >
          <Button
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 text-lg font-semibold"
          >
            Try HiveShare Free
          </Button>
          <p className="text-sm text-gray-500 mt-3">
            No credit card required • Set up in 5 minutes
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default DemoSection;